import React, { useMemo } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FiInfo } from "react-icons/fi";

import Checkbox from "../../../../../../../../../components/Form/Checkbox";
import Input from "../../../../../../../../../components/Form/Input";
import AddressInfoForm from "../../../../../../../../../components/Generic/AddressComponent/AddressInfoForm";
import SkeletonComponent from "../../../../../../../../../components/Generic/Skeleton";
import { DifferentLocation } from "../../../../../../../../../models/location";
import { Warehouse } from "../../../../../../../../../models/warehouse";
import useStyles from "./styles";

interface Props {
  warehouse?: Warehouse;
  isOnViewMode?: boolean;
  isUsingCompanyAddress?: boolean;
  isFetching?: boolean;
  hasCodeError?: boolean;
  setHasCodeError?: (hasCodeError: boolean) => void;
  companyLocation?: DifferentLocation;
  isLoading?: boolean;
  updateUser?: (value: boolean) => void;
  setIsUsingCompanyAddress: (value: boolean) => void;
  setWarehouse: (warehouse: Warehouse) => void;
  getErrorByField?: (message: string) => string | undefined;
}

const General = (props: Props) => {
  const {
    isOnViewMode,
    isFetching = false,
    warehouse,
    hasCodeError,
    setHasCodeError,
    isUsingCompanyAddress,
    companyLocation,
    updateUser,
    setIsUsingCompanyAddress,
    setWarehouse,
    getErrorByField,
    isLoading = false,
  } = props;
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const disabledFields = isOnViewMode || isLoading;

  const hasLocationError = (): boolean => {
    if (getErrorByField) {
      if (getErrorByField("location.longAddress") || getErrorByField("location")) {
        return true;
      }
    }

    return false;
  };

  const locationErrorMessage = (): string | undefined => {
    if (getErrorByField) {
      return getErrorByField("location.longAddress") || getErrorByField("location");
    }

    return "";
  };

  const addressArrayValue = useMemo((): DifferentLocation[] => {
    if (isUsingCompanyAddress && companyLocation) {
      return ([companyLocation] || []) as DifferentLocation[];
    }

    if (warehouse && warehouse?.location) {
      return [warehouse?.location];
    }

    return [] as DifferentLocation[];
  }, [warehouse, companyLocation, isUsingCompanyAddress]);

  return (
    <Grid container direction="row" alignItems="flex-start" spacing={2}>
      <Grid item md={6} lg={6}>
        <SkeletonComponent isLoading={isFetching}>
          <Input
            name="code"
            label={translate("code")}
            required
            disabled={disabledFields}
            placeholder={translate("code")}
            value={warehouse?.code || ""}
            onChange={(event) => {
              setHasCodeError?.(false);
              setWarehouse(
                warehouse
                  ? { ...warehouse, code: event.target.value }
                  : ({ code: event.target.value } as Warehouse)
              );
            }}
            error={!!(getErrorByField && getErrorByField("code")) || hasCodeError}
            errorMessage={getErrorByField && getErrorByField("code")}
          />
        </SkeletonComponent>
      </Grid>
      <Grid item md={6} lg={6}>
        <SkeletonComponent isLoading={isFetching}>
          <Input
            name="name"
            label={translate("name")}
            required
            placeholder={translate("name")}
            disabled={disabledFields}
            value={warehouse?.name || ""}
            onChange={(event) =>
              setWarehouse(
                warehouse
                  ? { ...warehouse, name: event.target.value }
                  : ({ name: event.target.value } as Warehouse)
              )
            }
            error={!!(getErrorByField && getErrorByField("name"))}
            errorMessage={getErrorByField && getErrorByField("name")}
          />
        </SkeletonComponent>
      </Grid>
      <Grid item md={12} lg={12}>
        <SkeletonComponent isLoading={isFetching}>
          <Input
            name="description"
            label={translate("description")}
            disabled={disabledFields}
            placeholder={translate("description")}
            value={warehouse?.description}
            onChange={(event) =>
              setWarehouse(
                warehouse
                  ? { ...warehouse, description: event.target.value }
                  : ({ description: event.target.value } as Warehouse)
              )
            }
            error={!!(getErrorByField && getErrorByField("description"))}
            errorMessage={getErrorByField && getErrorByField("description")}
          />
        </SkeletonComponent>
      </Grid>
      <Grid item md={12} lg={12}>
        <p className={classes.addressSectionTitle}>{translate("businessAddress")}</p>
        <SkeletonComponent isLoading={isFetching}>
          <>
            <Checkbox
              className="mt-2 mb-2"
              control={{
                "data-cy": "checkboxToUseCompanyAddress",
              }}
              label={translate("useCompanyAddress")}
              checked={isUsingCompanyAddress}
              disabled={disabledFields}
              onChange={(_, checked) => {
                if (setIsUsingCompanyAddress) setIsUsingCompanyAddress(checked);
                if (updateUser) updateUser(checked);
              }}
            />

            <AddressInfoForm
              hasError={hasLocationError()}
              addressesArray={addressArrayValue}
              containerClass="p-0"
              hasToShowOnlyOne={true}
              isExpandedAddress={true}
              condition=""
              view={isOnViewMode || isUsingCompanyAddress}
              update={(value: DifferentLocation[]) => {
                setWarehouse(
                  warehouse
                    ? { ...warehouse, location: value[0] }
                    : ({ location: value[0] } as Warehouse)
                );
              }}
            />
          </>
        </SkeletonComponent>
        {hasLocationError() && (
          <div className="mt-3">
            <span className={classes.error}>
              <FiInfo size={18} className="mr-2" />
              {locationErrorMessage() || ""}
            </span>
          </div>
        )}
      </Grid>
    </Grid>
  );
};

export default General;
